import React, { useState } from 'react';
import { WizardData } from '../types';

interface PersonalityStepProps {
  data: WizardData;
  updateData: (updates: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface PromptBuilderInputs {
  agentName: string;
  description: string;
}

const PersonalityStep: React.FC<PersonalityStepProps> = ({ 
  data, 
  updateData, 
  onNext, 
  onPrevious 
}) => {
  const [showAIAssistant, setShowAIAssistant] = useState(true);
  const [promptInputs, setPromptInputs] = useState<PromptBuilderInputs>({
    agentName: '',
    description: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [bestPractices, setBestPractices] = useState<string>('');

  React.useEffect(() => {
    // Load best practices from public file to include in system prompt context
    fetch('/prompt-best-practices.txt')
      .then((r) => r.text())
      .then((t) => setBestPractices(t))
      .catch(() => setBestPractices(''));
  }, []);

  const stripCodeFences = (text: string) => {
    if (!text) return text;
    // Remove leading/trailing triple backtick fences like ```md, ```mdx, ```
    const withoutLeadingFence = text.replace(/^\s*```[a-zA-Z]*\s*/i, '');
    const withoutTrailingFence = withoutLeadingFence.replace(/\s*```\s*$/i, '');
    return withoutTrailingFence.trim();
  };

  // Streamed Responses API helper
  const callOpenAIStream = async (
    messages: Array<{ role: string; content: string }>,
    onDelta: (deltaText: string) => void
  ) => {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      throw new Error('OpenAI API key not configured. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
    }

    const model = process.env.REACT_APP_OPENAI_MODEL || 'gpt-5';
    const isReasoningModel = model.toLowerCase().startsWith('gpt-5') || model.toLowerCase().startsWith('o');

    const controller = new AbortController();
    // Idle timeout: abort only if no chunks arrive for 30s
    let idleTimer: number = window.setTimeout(() => controller.abort(), 30000);

    try {
      const requestBody: any = {
        model,
        input: messages.map(m => ({ role: m.role, content: m.content })),
        // Short cap to enforce concise output
        max_output_tokens: 1200,
        stream: true,
        text: { format: { type: 'text' } }
      };

      // Add model-specific parameters
      if (isReasoningModel) {
        requestBody.reasoning = { effort: 'low' };
      } else {
        requestBody.temperature = 0.5;
      }

      const resp = await fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => '');
        throw new Error(`OpenAI API error: ${errText || resp.statusText}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let fullText = '';
      let responseId: string | null = null;

      while (true) {
        let done: boolean, value: Uint8Array | undefined;
        try {
          const res = await reader.read();
          done = res.done;
          value = res.value;
        } catch (e: any) {
          // Treat abort as graceful end if we already have text
          break;
        }
        if (done) break;
        buffer += decoder.decode(value!, { stream: true });

        // Reset idle timer on every chunk
        clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => controller.abort(), 60000);

        // Parse SSE lines
        const lines = buffer.split(/\r?\n/);
        // Keep the last partial line in buffer
        buffer = lines.pop() || '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data:')) continue;
          const jsonStr = trimmed.slice(5).trim();
          if (!jsonStr || jsonStr === '[DONE]') continue;
          try {
            const evt = JSON.parse(jsonStr);
            // Capture response id if present in any event payload
            if (typeof evt?.id === 'string' && String(evt.type || '').startsWith('response.')) {
              responseId = evt.id;
            } else if (typeof evt?.response?.id === 'string') {
              responseId = evt.response.id;
            }
            // Handle various delta shapes from Responses API
            // 1) response.output_text.delta
            if (evt.type === 'response.output_text.delta' && typeof evt.delta === 'string') {
              fullText += evt.delta;
              onDelta(evt.delta);
              continue;
            }
            // 2) message delta chunks
            if (evt.type === 'message.delta') {
              const content = Array.isArray(evt.delta?.content) ? evt.delta.content : [];
              for (const c of content) {
                if (typeof c?.text === 'string') {
                  fullText += c.text;
                  onDelta(c.text);
                }
                if (typeof c?.delta === 'string') {
                  fullText += c.delta;
                  onDelta(c.delta);
                }
              }
              continue;
            }
            // 3) direct text field
            if (typeof evt.text === 'string') {
              fullText += evt.text;
              onDelta(evt.text);
              continue;
            }
          } catch (_) {
            // Ignore JSON parse errors for non-data lines
          }
        }
      }

      // Fallback: if nothing streamed, try retrieving the final response
      if (!fullText.trim() && responseId) {
        try {
          const r = await fetch(`https://api.openai.com/v1/responses/${responseId}`, {
            headers: {
              'Authorization': `Bearer ${apiKey}`
            },
            signal: controller.signal
          });
          if (r.ok) {
            const d = await r.json();
            if (typeof d.output_text === 'string' && d.output_text.trim()) return d.output_text as string;
            const out = Array.isArray(d.output) ? d.output : [];
            for (const item of out) {
              const arr = Array.isArray(item?.content) ? item.content : [];
              for (const c of arr) {
                if (typeof c?.text === 'string' && c.text.trim()) return c.text as string;
              }
            }
          }
        } catch (_) {
          // ignore and fall through
        }
      }

      return fullText;
    } finally {
      clearTimeout(idleTimer);
    }
  };

  // Helper function to call OpenAI API
  const callOpenAI = async (messages: Array<{ role: string; content: string }>) => {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      throw new Error('OpenAI API key not configured. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
    }

    const model = process.env.REACT_APP_OPENAI_MODEL || 'gpt-5';
    const isReasoningModel = model.toLowerCase().startsWith('gpt-5') || model.toLowerCase().startsWith('o');

    // Abort after 60s to prevent infinite spinner
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);

    try {
      const requestBody: any = {
        model,
        input: messages.map(m => ({ role: m.role, content: m.content })),
        max_output_tokens: 1200,
        text: { format: { type: 'text' } }
      };

      // Add model-specific parameters
      if (isReasoningModel) {
        requestBody.reasoning = { effort: 'low' };
      } else {
        requestBody.temperature = 0.5;
      }

      // Use Responses API per official docs
      const resp = await fetch('https://api.openai.com/v1/responses', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        throw new Error(`OpenAI API error: ${errText || resp.statusText}`);
      }

      const data = await resp.json();

      // Prefer consolidated output_text when present
      if (typeof data.output_text === 'string' && data.output_text.trim().length > 0) {
        return data.output_text as string;
      }

      // Fallback: traverse output -> content -> text
      const output = Array.isArray(data.output) ? data.output : [];
      for (const item of output) {
        const contentArr = Array.isArray(item?.content) ? item.content : [];
        for (const c of contentArr) {
          if (typeof c?.text === 'string' && c.text.trim().length > 0) {
            return c.text as string;
          }
        }
      }

      throw new Error('OpenAI API returned no text.');
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const getUseCaseLabel = () => data.purpose?.replace('_', ' ') || 'general real estate assistance';

  const generatePrompt = async () => {
    setIsGenerating(true);
    console.log('Generating system prompt with Responses API');
    
    try {
      const agentName = (promptInputs.agentName || data.name || 'a professional real estate AI assistant').trim();
      const description = (promptInputs.description || '').trim();

      const systemMessage = `You are an expert at creating world-class system prompts for Conversational AI voice agents. Use the following best practices as authoritative guidance when constructing the prompt. Apply only five of the six building blocks (Personality, Environment, Tone, Goal, Guardrails) - exclude Tools. Optimize output for voice: natural prosody, TTS-friendly punctuation, spoken formatting of numbers/emails/URLs, brief check-ins, and adaptive tone.

Best practices (verbatim reference, do not echo unless helpful):\n\n${bestPractices.substring(0, 6000)}`;

      const userMessage = `Create a complete but concise system prompt for a real estate AI voice agent based on:
        - Agent Name: ${agentName}
        - Use Case: ${getUseCaseLabel()}
        - Description: ${description || 'Assist users effectively for this use case'}

        Requirements:
        - Structure with these sections only: # Personality, # Environment, # Tone, # Goal, # Guardrails
        - Do not include a # Tools section
        - Make it concrete to the use case; no generic boilerplate
        - Optimize for speech clarity and natural delivery
        - Include only the most important TTS considerations (1–3 bullets max)
        - Keep content actionable and unambiguous
        - Hard length limits:
          - Personality: max 3 bullets
          - Environment: max 2 bullets
          - Tone: max 4 bullets
          - Goal: max 3 sentences
          - Guardrails: exactly 4 bullets
        - Do not include meta-instructions or references to this request in the final prompt
        - Return PLAIN TEXT ONLY: do not wrap the result in backticks or code fences`;

      let accumulated = '';
      const append = (delta: string) => {
        accumulated += delta;
        const preview = stripCodeFences(accumulated);
        updateData({ systemPrompt: preview });
      };

      const full = await callOpenAIStream([
        { role: 'system', content: systemMessage },
        { role: 'user', content: userMessage }
      ], append);

      const cleaned = stripCodeFences(full);
      updateData({ systemPrompt: cleaned });
      setIsGenerating(false);
    } catch (error: any) {
      console.error('Error generating prompt:', error);
      alert(error.message || 'Failed to generate prompt. Please check your OpenAI API key.');
      setIsGenerating(false);
    }
  };

  const handleIteratePrompt = async () => {
    if (!customInstructions.trim()) return;
    
    setIsGenerating(true);
    console.log('Modifying prompt with Responses API based on user instructions');
    
    try {
      const systemMessage = `You are an expert at modifying system prompts for conversational AI agents. You will receive an existing system prompt and user instructions for how to modify it. Your task is to update the prompt according to the user's instructions while maintaining the overall structure and quality of the prompt. Keep all the original content except for the specific changes requested by the user.`;
      
      const userMessage = `Here is the current system prompt:

${data.systemPrompt}

User's modification request: "${customInstructions}"

Please update the prompt according to the user's request while keeping everything else the same. Return the complete modified prompt.`;

      let accumulated = '';
      const append = (delta: string) => {
        accumulated += delta;
        const preview = stripCodeFences(accumulated);
        updateData({ systemPrompt: preview });
      };

      const full = await callOpenAIStream([
        { role: 'system', content: systemMessage },
        { role: 'user', content: userMessage }
      ], append);

      const cleaned = stripCodeFences(full);
      updateData({ systemPrompt: cleaned });
      setCustomInstructions('');
      setIsGenerating(false);
    } catch (error: any) {
      console.error('Error modifying prompt:', error);
      alert(error.message || 'Failed to modify prompt. Please check your OpenAI API key.');
      setIsGenerating(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (data.systemPrompt) {
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-xl font-semibold text-white flex items-center">
          <span className="mr-2">✨</span> Agent Personality & Behavior
        </h4>
        <button
          type="button"
          onClick={() => setShowAIAssistant(!showAIAssistant)}
          className="text-sm px-3 py-1 bg-blue-600/20 text-blue-400 border border-blue-600 rounded-lg hover:bg-blue-600/30 transition-colors"
        >
          {showAIAssistant ? '✏️ Manual Edit' : '🤖 AI Assistant'}
        </button>
      </div>

      {/* AI Assistant */}
      {showAIAssistant && (
        <div className="bg-gray-800 rounded-xl p-6 space-y-4 border border-gray-600">
          <h5 className="text-lg font-semibold text-blue-400 mb-4">
            AI Prompt Builder
          </h5>
          
          <p className="text-sm text-gray-400 mb-4">
            Name your agent and briefly describe what you want it to do. We'll generate a best-practices system prompt using your selected use case and description.
          </p>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs text-gray-300 mb-1">Agent Name</label>
              <input
                type="text"
                placeholder="e.g., Sarah"
                value={promptInputs.agentName}
                onChange={(e) => setPromptInputs({...promptInputs, agentName: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-300 mb-1">Agent Description</label>
              <textarea
                placeholder="Describe what the agent should do..."
                value={promptInputs.description}
                onChange={(e) => setPromptInputs({...promptInputs, description: e.target.value})}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={4}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={generatePrompt}
            disabled={isGenerating}
            className="w-full mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Generating this may take up to a minute...
              </span>
            ) : (
              '✨ Generate System Prompt'
            )}
          </button>
        </div>
      )}

      {/* Iteration Section - Now on top */}
      {data.systemPrompt && (
        <div className="bg-gray-800 rounded-xl p-3 border border-gray-600">
          <div className="flex gap-2">
            <input
              type="text"
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="Tell the AI what you'd like to change about the prompt..."
              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-500 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={handleIteratePrompt}
              disabled={isGenerating || !customInstructions.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      )}

      {/* System Prompt */}
      <div>
        <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-300 mb-2">
          System Prompt *
        </label>
        <textarea
          id="systemPrompt"
          value={data.systemPrompt}
          onChange={(e) => updateData({ systemPrompt: e.target.value })}
          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-sm"
          rows={12}
          placeholder="Click 'Generate System Prompt' above to create a professional prompt using AI, or write your own."
          required
        />
        <p className="mt-1 text-sm text-gray-400">
          This prompt defines how your agent behaves, communicates, and assists clients.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onPrevious}
          className="bg-gray-700 hover:bg-gray-600 text-white font-medium px-6 py-3 rounded-xl transition-colors"
        >
          ← Previous
        </button>
        <button
          type="submit"
          disabled={!data.systemPrompt}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
        >
          Next Step →
        </button>
      </div>
    </form>
  );
};

export default PersonalityStep;